import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const GROUP_IMG =
  "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/6i2tdaxi_IMG_1564.JPG";
const EXPANSIVE_BG =
  "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=2400&q=80";
const HERO_ILLUSTRATION =
  "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/yo5juwnt_ChatGPT%20Image%20May%2022%2C%202026%20at%2001_50_41%20PM.png";

const PILLARS = [
  { n: "I", label: "Connect", text: "Bringing humans together across boundaries." },
  { n: "II", label: "Explore", text: "Asking deeper questions about what shapes us." },
  { n: "III", label: "Co-create", text: "Designing meaning, structure and shared form." },
  { n: "IV", label: "Nourish", text: "Holding ground for honesty, presence and care." },
  { n: "V", label: "Evolve", text: "Growing slowly. With intentionality and patience." },
];

export const Harmonia = () => {
  return (
    <section
      id="harmonia"
      data-testid="harmonia-section"
      className="relative bg-forest text-bone py-28 md:py-44 overflow-hidden"
    >
      {/* Background: deep forest, heavily darkened so all text is highly readable */}
      <div className="absolute inset-0 -z-0 bg-forest">
        <img src={EXPANSIVE_BG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-forest/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-forestDeep/60 via-transparent to-forestDeep/70" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 text-center">
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6"
        >
          ✦ &nbsp; 05 , Harmonia
        </motion.p>

        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          data-testid="harmonia-title"
          className="font-serif text-6xl md:text-9xl text-bone leading-[0.9] tracking-tight"
        >
          <span className="italic">Harmonia</span>
        </motion.h2>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mt-6 text-[11px] uppercase tracking-[0.3em] text-clay/80"
        >
          A living invitation, not a project
        </motion.p>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          data-testid="harmonia-description"
          className="mt-12 max-w-3xl mx-auto text-lg md:text-xl text-bone/95 leading-relaxed font-light text-pretty"
        >
          A relational ecosystem exploring{" "}
          <span className="font-serif italic text-bronze text-[1.4em]">
            new models of care
          </span>
          , collaboration, and community design.
        </motion.p>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mt-6 max-w-2xl mx-auto text-base text-bone/75 leading-relaxed font-light"
        >
          We gather, slowly. We listen, deeply. We build only what feels true,
          rooms, rituals, frameworks and shared practices for people moving
          from fragmentation to wholeness.
        </motion.p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.1)}
          className="mt-20 grid grid-cols-2 md:grid-cols-5 gap-10 max-w-5xl mx-auto"
        >
          {PILLARS.map((p) => (
            <motion.div
              key={p.n}
              variants={fadeUp}
              data-testid={`harmonia-pillar-${p.label.toLowerCase()}`}
            >
              <p className="font-serif italic text-bronze text-2xl">{p.n}</p>
              <h4 className="mt-2 font-serif text-2xl text-bone">{p.label}</h4>
              <p className="mt-2 text-xs text-bone/70 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mt-20"
        >
          <Link
            to="/harmonia"
            data-testid="harmonia-cta"
            className="btn-pill inline-flex items-center gap-3 px-8 py-4 rounded-full bg-bone text-forest text-[11px] uppercase tracking-[0.22em] hover:bg-bronze hover:text-bone"
          >
            Step into the Harmonia portal
          </Link>
          <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-clay/70 italic font-serif">
            ✦ &nbsp; from intention to impact, together &nbsp; ✦
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Harmonia;
